import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, FileText, History, Loader2, Mic, Minus, Paperclip, Save, Search, Sparkles, Square, Trash2, Wand2, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/v5/components/ui/sheet";
import { LabPdfPreviewPanel } from "@/pages/patients/labs/LabPdfPreviewPanel";
import { isPreviewableFilename } from "@/lib/previewableFiles";
import { useNoteForm } from "@/v5/contexts/NoteFormContext";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/v5/components/ui/button";
import { Input } from "@/v5/components/ui/input";
import { Label } from "@/v5/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/v5/components/ui/select";
import { SearchableCombobox, type ComboboxOption } from "@/components/ui/SearchableCombobox";
import { Checkbox } from "@/v5/components/ui/checkbox";
import { Separator } from "@/v5/components/ui/separator";
import { Spinner } from "@/v5/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/v5/components/ui/tabs";
import { Textarea } from "@/v5/components/ui/textarea";
import { Badge } from "@/v5/components/ui/badge";
import { TiptapEditor } from "@/v5/components/ui/tiptap-editor";
import type { Editor } from "@/v5/components/ui/tiptap-editor";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/v5/components/ui/collapsible";
import { SaveTemplateDialog } from "@/v5/components/SaveTemplateDialog";
import { CameraCaptureModal } from "@/components/medical/CameraCaptureModal";
import { FileUpload } from "@/components/ui/FileUpload";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { pacificToWallClock, reprojectDateTime } from "@/lib/facilityTimezone";
import { formatNoteApptLink } from "@/v5/lib/noteAppointmentLabel";
import type {
  EFormPlan,
  FormatOption,
  NoteCategory,
  NoteHistoryEntry,
  NoteTemplate,
  PatientNoteDetail,
  SurveyOption,
  TranscriptEntry,
} from "@/v5/services/notes.service";
import * as notesSvc from "@/v5/services/notes.service";
import { TranscribeSession, listAudioInputs } from "@/v5/lib/transcribeStream";
import type { LiveTranscriptItem, TranscribeSessionStatus } from "@/v5/lib/transcribeStream";
import { describeDictationError } from "@/v5/lib/dictationErrors";
import { AI_NOT_CONFIGURED_MESSAGE, describeNoteAiError } from "@/v5/lib/noteAiErrors";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/v5/components/ui/alert-dialog";
import { PrintOptionsDialog } from "@/v5/components/PrintOptionsDialog";
import { hold, release } from "@/lib/idleSession";
import { toast } from "@/store/toastStore";
import { scrollElementIntoCenter } from "@/v5/lib/scrollWithin";
import { DatePicker } from "@/components/ui/DatePicker";
import { wallClockPartsInTz } from "@/lib/facilityTimezone";

interface NoteFormModalProps {
  patientId: string;
  noteId: number | null;
  autoRecord?: boolean;
  /** When set and this is a NEW note, the created note is linked to this appointment. */
  linkAppointmentId?: number;
  /** Chip label; falls back to the loaded note's own first linked appointment. */
  linkAppointmentLabel?: string;
  onClose: () => void;
  onMinimize?: () => void;
}

interface RecordedEntry {
  id: string;
  speakerLabel: string;
  text: string;
  startMs: number | null;
  endMs: number | null;
}

/**
 * Streaming Transcribe labels voices "0", "1", … (batch jobs use "spk_0").
 * It tells voices apart but not who is who, so lines read "Speaker 1" /
 * "Speaker 2" rather than a guessed Provider / Patient that would mislabel a
 * patient who spoke first.
 */
function speakerName(label: string): string {
  const m = /^(?:spk_)?(\d+)$/.exec(label ?? "");
  return m ? `Speaker ${Number(m[1]) + 1}` : "Speaker";
}

/** A one-voice dictation reads as plain text, without labels. */
function hasSeveralSpeakers(entries: { speakerLabel: string }[]): boolean {
  return new Set(entries.map((e) => e.speakerLabel)).size > 1;
}

function transcriptLine(e: { speakerLabel: string; text: string }, labelled: boolean): string {
  return labelled ? `${speakerName(e.speakerLabel)}: ${e.text}` : e.text;
}

/** The edited transcript back into saveable entries, one per non-blank line. */
function entriesFromDraft(draft: string): TranscriptEntry[] {
  return draft
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = /^Speaker (\d+):\s*(.*)$/.exec(line);
      return m
        ? { speakerLabel: String(Number(m[1]) - 1), text: m[2].trim(), startMs: null, endMs: null }
        : { speakerLabel: "", text: line, startMs: null, endMs: null };
    })
    .filter((e) => e.text !== "");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** The text an AI prompt runs on: the dictation transcript or the typed note. */
type AiSource = "transcript" | "note";

/** Always offered under AI Options, before and alongside the AI's own suggestions. */
const STANDARD_AI_PROMPTS: { id: string; label: string; prompt: string }[] = [
  {
    id: "phone-call-note",
    label: "Phone call note",
    prompt:
      "Write a concise phone call note: who called and why, what was discussed, any advice given, the plan, and follow-up. Do not add details that are not in the transcript.",
  },
  {
    id: "soap-note",
    label: "SOAP note",
    prompt:
      "Write a SOAP note with a heading for each section (Subjective, Objective, Assessment, Plan). Keep a section brief when the transcript has nothing for it, and do not add findings that are not in the transcript.",
  },
  {
    id: "visit-summary",
    label: "Visit summary",
    prompt:
      "Write a short visit summary for the chart: the reason for the visit, the key points discussed, and the outcome. Do not add details that are not in the transcript.",
  },
  {
    id: "follow-up-plan",
    label: "Follow-up plan",
    prompt:
      "List the follow-up plan: action items, the next appointment or its timing, and any instructions given to the patient. Do not add details that are not in the transcript.",
  },
];

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function plainTranscript(entries: RecordedEntry[]): string {
  const labelled = hasSeveralSpeakers(entries);
  return entries.map((e) => transcriptLine(e, labelled)).join("\n");
}

const PACIFIC_TZ = "America/Los_Angeles";

function nowInTz(tz: string): { date: string; time: string } {
  const now = new Date();
  // A zone that can't be read falls back to this computer's own clock.
  const p = wallClockPartsInTz(now, tz) ?? {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: now.getHours(),
    minute: now.getMinutes(),
  };
  const pad = (n: number) => String(n).padStart(2, "0");
  return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}` };
}

export function NoteFormModal({
  patientId,
  noteId,
  autoRecord,
  linkAppointmentId,
  linkAppointmentLabel,
  onClose,
  onMinimize,
}: NoteFormModalProps) {
  const isEdit = noteId !== null;
  const editorRef = useRef<Editor | null>(null);
  const editorHadFocus = useRef(false);
  const noteForm = useNoteForm();
  const userTz = useUserTimezone();
  const currentUserId = useAuthStore((s) => s.user?.id) ?? 0;
  // We track "the user has interacted" with a ref so the dirty flag only flips
  // after the initial async load has populated state, not as a side-effect of
  // the hydration itself.
  const dirtyArmedRef = useRef(false);

  // ─── Form state ──────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [printNoteId, setPrintNoteId] = useState<number | null>(null);
  const [note, setNote] = useState<PatientNoteDetail | null>(null);
  // Prefer the label the opener supplied (a new note has no saved link yet); fall
  // back to the loaded note's own first link so a note opened from the patient
  // Notes tab still shows which appointment it belongs to.
  const apptChipLabel =
    linkAppointmentLabel ?? formatNoteApptLink(note?.appointments?.[0], userTz);
  const [categories, setCategories] = useState<NoteCategory[]>([]);
  const [templates, setTemplates] = useState<NoteTemplate[]>([]);
  const [history, setHistory] = useState<NoteHistoryEntry[]>([]);

  const [typeId, setTypeId] = useState("0");
  const [title, setTitle] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ title?: string; typeId?: string }>({});
  const subjectRef = useRef<HTMLInputElement>(null);
  const typeRef = useRef<HTMLDivElement>(null);
  /** The form body, which is the scrollport the validation jump must stay inside. */
  const formScrollerRef = useRef<HTMLDivElement>(null);
  const [content, setContent] = useState("");
  const [comments, setComments] = useState("");
  const [noteDate, setNoteDate] = useState(() => nowInTz(userTz).date);
  const [noteTime, setNoteTime] = useState(() => nowInTz(userTz).time);
  const [visibleToPatient, setVisibleToPatient] = useState(false);
  const [capturePhoto, setCapturePhoto] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [removeFileIds, setRemoveFileIds] = useState<number[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);

  // Post-save camera capture. When the "Capture photo(s) from device after
  // saving" checkbox is on, we open CameraCaptureModal in multi-shot mode
  // scoped to the freshly-saved note and only close NoteFormModal once the
  // user is done capturing. Replaces the legacy
  // window.open('/patient_photo_capture.php?...') which 404s on the v5 SPA
  // host (no PHP runtime, no Laravel route).
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraNoteId, setCameraNoteId] = useState<number | null>(null);

  // ─── Templates sidebar ───────────────────────────────────────────────────
  const [tplTab, setTplTab] = useState<"all" | "office" | "mine" | "suggested">("all");
  const [tplSearch, setTplSearch] = useState("");
  const [tplTypeFilter, setTplTypeFilter] = useState<string>("all");
  const [tplSuggestions, setTplSuggestions] = useState<{ templateId: number; score: number; reason: string | null }[]>([]);

  // ─── Recording / transcription ───────────────────────────────────────────
  const sessionRef = useRef<TranscribeSession | null>(null);
  const [audioInputs, setAudioInputs] = useState<{ deviceId: string; label: string }[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [recStatus, setRecStatus] = useState<TranscribeSessionStatus | "idle">("idle");
  const [partialEntry, setPartialEntry] = useState<LiveTranscriptItem | null>(null);
  const [recordedEntries, setRecordedEntries] = useState<RecordedEntry[]>([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const startedAtRef = useRef<number>(0);
  // The transcript as the user sees it after Stop. It follows the recorded
  // entries until the user edits it; from then on it is theirs, and anything
  // recorded later is appended. Insert, the AI options and the saved
  // transcript all read it.
  const [transcriptDraft, setTranscriptDraft] = useState("");
  const draftEditedRef = useRef(false);
  const draftedCountRef = useRef(0);
  // Read by the post-Stop suggestions, which fire from a timer and from
  // controls registered once at mount, so they must not read render state.
  const transcriptDraftRef = useRef("");
  transcriptDraftRef.current = transcriptDraft;

  // ─── AI availability ─────────────────────────────────────────────────────
  // Set when the server answers AI_NOT_CONFIGURED, cleared when an AI call
  // succeeds. Only the background suggestions skip on it: Record and the
  // prompts always ask the server, so keys an administrator saves while the
  // note is open work without closing it.
  const aiUnavailableRef = useRef(false);
  const [aiBlockedOpen, setAiBlockedOpen] = useState(false);

  // ─── AI suggestions sidebar ──────────────────────────────────────────────
  const [aiOptions, setAiOptions] = useState<FormatOption[]>([]);
  const [aiOptionsLoading, setAiOptionsLoading] = useState(false);
  const [aiOptionsError, setAiOptionsError] = useState("");
  // Plain text of the editor captured when focus enters, used to detect whether
  // the user actually changed the note before running AI on blur.
  const contentAtFocusRef = useRef<string>("");
  // The exact text last fed to the AI. Guards against re-running suggestions on
  // an identical blur.
  const aiSourceRef = useRef<string>("");
  // What the user last worked on: the transcript (Stop, or an edit to it) or
  // the typed note (leaving the editor after a change). The standard and
  // custom prompts run on it.
  const lastSourceRef = useRef<AiSource | null>(null);
  // What the listed suggestions were made from. Picking one runs on that
  // text as it reads now, so later edits to it are included.
  const aiOptionsSourceRef = useRef<AiSource | null>(null);
  // Always points at the latest runSuggestions closure so the editor blur
  // handler (attached once) invokes a version that reads current state.
  const runSuggestionsRef = useRef<(src: string, from: AiSource) => void>(() => {});
  const editorHandlersAttached = useRef(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Existing attachment being previewed, shown as an overlay over this form.
  const [previewAttachment, setPreviewAttachment] = useState<
    { attachmentId: number; filename: string } | null
  >(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLabel, setPreviewLabel] = useState("");

  // ─── E-Forms panel ───────────────────────────────────────────────────────
  const [eformsOpen, setEformsOpen] = useState(false);
  const [eformsLoading, setEformsLoading] = useState(false);
  const [eformPlans, setEformPlans] = useState<EFormPlan[]>([]);
  const [eformSurveys, setEformSurveys] = useState<SurveyOption[]>([]);
  const [eformSelectedPlan, setEformSelectedPlan] = useState<string>("0");
  const [eformLinkBusy, setEformLinkBusy] = useState<number | null>(null);

  // E-form iframe modal state. Mirrors legacy displaySurveyForNote(): the
  // form opens in an iframe inside a modal, the parent listens for the
  // postMessage with the rendered HTML, calls /eforms/replace-keywords with
  // the real patientId + the plan id captured AT CLICK TIME (notePlanIDRef,
  // not eformSelectedPlan — the user might change the dropdown after click),
  // and inserts the substituted HTML at the cursor.
  const [eformIframe, setEformIframe] = useState<{ url: string; title: string } | null>(null);
  const [eformInserting, setEformInserting] = useState(false);
  const notePlanIDRef = useRef<number>(0);

  // ─── Initial load ────────────────────────────────────────────────────────
  // Use allSettled so a failure in one sibling fetch (e.g. /notes/templates
  // returning 404) does not silently wipe out the other successful results
  // (e.g. /note-categories returning 31 rows).
  const loadData = useCallback(async () => {
    try {
      const [catRes, tplRes] = await Promise.allSettled([
        notesSvc.listNoteCategories(),
        notesSvc.listTemplates(),
      ]);

      if (catRes.status === "fulfilled") {
        setCategories(catRes.value);
      } else {
        console.error("[NoteFormModal] listNoteCategories failed:", catRes.reason);
        toast.error("Failed to load note types");
      }

      if (tplRes.status === "fulfilled") {
        setTemplates(tplRes.value);
      } else {
        console.error("[NoteFormModal] listTemplates failed:", tplRes.reason);
      }

      if (isEdit && noteId) {
        const [noteRes, histRes] = await Promise.allSettled([
          notesSvc.getNote(patientId, noteId),
          notesSvc.getNoteHistory(patientId, noteId),
        ]);
        if (noteRes.status === "fulfilled") {
          const noteData = noteRes.value;
          setNote(noteData);
          setTypeId(String(noteData.typeId));
          setTitle(noteData.title);
          setContent(noteData.content);
          setComments(noteData.comments);
          setVisibleToPatient(noteData.visibleToPatient);
          if (noteData.noteDate) {
            const raw = noteData.noteDate.replace(" ", "T");
            const pacDate = raw.slice(0, 10);
            const pacTime = raw.slice(11, 16) || "00:00";
            const wall = pacificToWallClock(pacDate, pacTime, userTz);
            setNoteDate(wall.date);
            setNoteTime(wall.time);
          }
        } else {
          console.error("[NoteFormModal] getNote failed:", noteRes.reason);
          toast.error("Failed to load note");
        }
        if (histRes.status === "fulfilled") {
          setHistory(histRes.value);
        } else {
          console.error("[NoteFormModal] getNoteHistory failed:", histRes.reason);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [patientId, noteId, isEdit]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Light refetch used after Save-as-Template so a freshly saved template
  // appears in the picker without the user closing and reopening the modal.
  const refetchTemplates = useCallback(async () => {
    try {
      const tpls = await notesSvc.listTemplates();
      setTemplates(tpls);
    } catch (e) {
      console.error("[NoteFormModal] refetchTemplates failed:", e);
    }
  }, []);

  // Tear down any active session if the modal is closed mid-recording.
  useEffect(() => {
    return () => {
      void sessionRef.current?.stop();
      sessionRef.current = null;
    };
  }, []);

  // Keep the draft in step with the recording. Until the user edits it, it is
  // rebuilt from every entry, so the labels follow when a second voice joins.
  // After an edit, only lines recorded since are appended.
  useEffect(() => {
    if (!draftEditedRef.current) {
      setTranscriptDraft(plainTranscript(recordedEntries));
    } else if (recordedEntries.length > draftedCountRef.current) {
      const labelled = hasSeveralSpeakers(recordedEntries);
      const added = recordedEntries.slice(draftedCountRef.current).map((e) => transcriptLine(e, labelled));
      setTranscriptDraft((prev) => [prev.trimEnd(), ...added].filter(Boolean).join("\n"));
    }
    draftedCountRef.current = recordedEntries.length;
  }, [recordedEntries]);

  function handleDraftChange(next: string) {
    draftEditedRef.current = true;
    lastSourceRef.current = "transcript";
    setTranscriptDraft(next);
    // An edited transcript is unsaved work: closing the note must ask first.
    noteForm.setIsDirty(true);
  }

  // Tick the elapsed counter while listening.
  useEffect(() => {
    if (recStatus !== "listening") return;
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 500);
    return () => window.clearInterval(id);
  }, [recStatus]);

  // Mirror recording state into the floating-note context so the minimized
  // card can show a live red dot while the user is on another route.
  //
  // The same flag holds the idle-logout timer open: dictating produces no pointer
  // or key events (and OS speech-to-text produces no keydown either), so without
  // the hold a long dictation would be cut off by an auto-logout and the
  // recording lost. Memory-only and capped — see lib/idleSession.ts.
  useEffect(() => {
    const isRec =
      recStatus === "listening" ||
      recStatus === "requesting-mic" ||
      recStatus === "stopping";
    noteForm.setIsRecording(isRec);

    if (!isRec) return;
    hold("note-dictation");
    return () => release("note-dictation");
  }, [recStatus, noteForm]);

  // Mirror the subject so the minimized card can label itself meaningfully.
  useEffect(() => {
    noteForm.setDraftSubject(title);
  }, [title, noteForm]);

  // Arm dirty tracking only after initial hydration, then flip on any real
  // user-driven edit. Used by the singleton confirm in NoteFormContext.
  useEffect(() => {
    if (loading) return;
    if (!dirtyArmedRef.current) {
      dirtyArmedRef.current = true;
      return;
    }
    noteForm.setIsDirty(true);
  }, [title, content, comments, typeId, loading, noteForm]);

  // Attach focus/blur handlers to the editor exactly once (editorRef is a ref,
  // so this no-dep effect polls each render until the editor instance exists).
  // On focus we snapshot the note text; on blur we run AI suggestions if the
  // user changed the note — mirroring how dictation triggers them post-stop.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editorHandlersAttached.current) return;
    editorHandlersAttached.current = true;

    const onFocus = () => {
      editorHadFocus.current = true;
      contentAtFocusRef.current = editor.getText();
    };
    const onBlur = () => {
      const text = editor.getText();
      if (!text.trim()) return;
      if (text === contentAtFocusRef.current) return; // no edit while focused
      lastSourceRef.current = "note";
      if (text === aiSourceRef.current) return; // already ran on this exact text
      runSuggestionsRef.current(text, "note");
    };

    editor.on("focus", onFocus);
    editor.on("blur", onBlur);
    return () => {
      editor.off("focus", onFocus);
      editor.off("blur", onBlur);
      editorHandlersAttached.current = false;
    };
  });

  // ─── Type list filtering (legacy parity: only non-lab in NEW mode) ───────
  const visibleCategories = useMemo(() => {
    return isEdit ? categories : categories.filter((c) => c.isLab === 0);
  }, [categories, isEdit]);

  // Searchable Type combobox options (same shape used on the Payments modal).
  // Use string values to match `typeId` and avoid the `42 === "42"` mismatch
  // when Eloquent serializes noteTypeID without an integer cast.
  const typeOptions = useMemo<ComboboxOption<string>[]>(
    () => visibleCategories.map((c) => ({ value: String(c.noteTypeID), label: c.name })),
    [visibleCategories],
  );

  // ─── Template filtering ──────────────────────────────────────────────────
  // The server returns only Office templates and the user's own, so "All" is
  // that whole list. Templates are further scoped by category (synced to the
  // form's Note Type) and by an optional search term.
  const filteredTemplates = useMemo(() => {
    // Ownership filter driven by the active segment (All / Office / Mine).
    // "Suggested" uses a separate list, so it is not handled here.
    const byOwner = tplTab === "office"
      ? templates.filter((t) => t.isShared)
      : tplTab === "mine"
      ? templates.filter((t) => !t.isShared && t.userId === currentUserId)
      : templates;
    const byType = tplTypeFilter === "all"
      ? byOwner
      : byOwner.filter((t) => String(t.categoryId ?? "") === tplTypeFilter);
    const term = tplSearch.trim().toLowerCase();
    if (!term) return byType;
    return byType.filter((t) =>
      (t.title || "").toLowerCase().includes(term) ||
      (t.categoryName || "").toLowerCase().includes(term)
    );
  }, [templates, tplTab, tplTypeFilter, tplSearch, currentUserId]);

  // Mirror the form's Note Type into the right-side category filter so
  // selecting a Note Type automatically narrows the template list to that
  // category. Stay one-way (form → sidebar): the user can still browse a
  // different category in the sidebar without changing the saved Note Type.
  useEffect(() => {
    setTplTypeFilter(typeId === "0" ? "all" : typeId);
  }, [typeId]);

  const suggestedTemplates = useMemo(() => {
    if (tplSuggestions.length === 0) return [];
    const byId = new Map(templates.map((t) => [t.templateId, t] as const));
    return tplSuggestions
      .map((s) => ({ tpl: byId.get(s.templateId), score: s.score, reason: s.reason }))
      .filter((x): x is { tpl: NoteTemplate; score: number; reason: string | null } => !!x.tpl);
  }, [tplSuggestions, templates]);

  // ─── Save handler ────────────────────────────────────────────────────────
  const persistTranscript = useCallback(
    async (savedNoteId: number) => {
      if (recordedEntries.length === 0) return;
      // An edited transcript is saved as edited: its lines no longer line up
      // with the recorded timings, so those are dropped.
      const entries: TranscriptEntry[] = draftEditedRef.current
        ? entriesFromDraft(transcriptDraft)
        : recordedEntries.map((e) => ({
            speakerLabel: e.speakerLabel,
            text: e.text,
            startMs: e.startMs,
            endMs: e.endMs,
          }));
      if (entries.length === 0) return;
      try {
        await notesSvc.saveTranscriptEntries(patientId, savedNoteId, { entries });
      } catch (err) {
        // Soft-fail: the editor content is the canonical source; the
        // transcript is supplemental.

        console.error("Failed to save transcript entries", err);
      }
    },
    [recordedEntries, transcriptDraft, patientId]
  );

  async function handleSave(andPrint = false) {
    const newErrors: { title?: string; typeId?: string } = {};
    if (!title.trim()) newErrors.title = "Subject is required.";

    // Auto-classify before save when type is unset and we have a transcript
    let resolvedTypeId = typeId;
    if (typeId === "0" && recordedEntries.length > 0 && transcriptDraft.trim() && !aiUnavailableRef.current) {
      try {
        const result = await notesSvc.classifyNoteType(
          transcriptDraft,
          visibleCategories.map((c) => ({ noteTypeId: c.noteTypeID, name: c.name }))
        );
        if (result.noteTypeId && (result.confidence === "high" || result.confidence === "medium")) {
          resolvedTypeId = String(result.noteTypeId);
          setTypeId(resolvedTypeId);
        }
      } catch {
        // Non-fatal — the user-supplied type still controls.
      }
    }
    if (resolvedTypeId === "0") newErrors.typeId = "Please select a note type.";

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      const firstRef = newErrors.typeId ? typeRef : subjectRef;
      // Scoped to the form body. `scrollIntoView` moves every scrollable ancestor on
      // both axes, and this form is a modal over a page that is itself scrollable —
      // so jumping to the first invalid field also dragged the page behind the
      // modal, which is visible the moment the modal closes.
      const scroller = formScrollerRef.current;
      if (scroller && firstRef.current) {
        scrollElementIntoCenter(scroller, firstRef.current);
      }
      if (newErrors.title && !newErrors.typeId) subjectRef.current?.focus();
      toast.error(newErrors.title ?? newErrors.typeId ?? "Please complete the required fields.");
      return;
    }
    setFieldErrors({});

    setSaving(true);
    try {
      const editorContent = editorRef.current?.getHTML() ?? content;
      console.info("[NoteSave] submitting", {
        patientId,
        isEdit,
        typeId: resolvedTypeId,
        contentChars: editorContent.length,
        contentBytes: new Blob([editorContent]).size,
      });
      const pac = reprojectDateTime(noteDate, noteTime, userTz, PACIFIC_TZ);
      let savedNote: PatientNoteDetail;

      if (isEdit && noteId) {
        // Deliberately does NOT send linkAppointmentId. The only way to reach this
        // branch with appointment context is click-through from the appointment
        // modal's linked-notes list, where the link already exists — so sending it
        // would be a no-op at best, and link changes are owned by the appointment
        // side (POST/DELETE /appointments/{id}/notes). See UpdateNotePayload.
        savedNote = await notesSvc.updateNote(patientId, noteId, {
          title,
          content: editorContent,
          comments,
          typeId: parseInt(resolvedTypeId),
          noteDate: pac.date,
          noteTime: pac.time,
          removeFiles: removeFileIds.length > 0 ? removeFileIds : undefined,
        });
        if (pendingFiles.length > 0) {
          await Promise.all(pendingFiles.map((f) => notesSvc.uploadAttachment(patientId, noteId, f)));
        }
      } else {
        savedNote = await notesSvc.createNote(patientId, {
          title,
          content: editorContent,
          comments,
          typeId: parseInt(resolvedTypeId),
          noteDate: pac.date,
          noteTime: pac.time,
          visibleToPatient,
          // Undefined is dropped by JSON.stringify in v5/lib/api.ts, so the
          // patient-tab path sends an unchanged body.
          appointmentId: linkAppointmentId,
        });
        if (pendingFiles.length > 0) {
          await Promise.all(pendingFiles.map((f) => notesSvc.uploadAttachment(patientId, savedNote.noteId, f)));
        }
      }

      await persistTranscript(savedNote.noteId);

      if (andPrint) setPrintNoteId(savedNote.noteId);
      toast.success(isEdit ? "Note updated" : "Note created");
      noteForm.setIsDirty(false);

      if (capturePhoto) {
        setCameraNoteId(savedNote.noteId);
        setCameraOpen(true);
        return;
      }
      noteForm.forceClose();
    } catch (err) {
      console.error("[NoteSave] failed", err);
      // An attachment failure arrives after the note itself saved, so "Failed to
      // save note" would be false and invite a retry that files a duplicate.
      toast.error(
        err instanceof notesSvc.AttachmentUploadError
          ? `The note was saved. ${err.message}`
          : `Failed to save note: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleApplyTemplate(templateId: number) {
    try {
      const rendered = await notesSvc.renderTemplate(templateId, patientId);
      if (!title.trim()) setTitle(rendered.title);

      const editor = editorRef.current;
      if (editor) {
        editor
          .chain()
          .focus(editorHadFocus.current ? undefined : "end")
          .insertContent(rendered.content, {
            parseOptions: { preserveWhitespace: "full" },
          })
          .run();
        setContent(editor.getHTML());
      } else {
        setContent((prev) => prev + rendered.content);
      }
      toast.success("Template applied");
    } catch {
      toast.error("Failed to load template");
    }
  }

  async function handleDeleteTemplate(templateId: number) {
    try {
      await notesSvc.deleteTemplate(templateId);
      await refetchTemplates();
      setTplSuggestions((prev) => prev.filter((s) => s.templateId !== templateId));
      toast.success("Template deleted");
    } catch {
      toast.error("Failed to delete template");
    }
  }

  // ─── Visible-to-Patient toggle (immediate persist in edit mode) ──────────
  async function handleVisibilityToggle() {
    const next = !visibleToPatient;
    setVisibleToPatient(next);
    if (isEdit && noteId) {
      try {
        const result = await notesSvc.togglePortalVisibility(patientId, noteId);
        setVisibleToPatient(result.visible);
      } catch {
        setVisibleToPatient(!next);
        toast.error("Failed to update portal visibility");
      }
    }
  }

  // ─── Recording controls ──────────────────────────────────────────────────
  async function ensureAudioInputs() {
    try {
      const list = await listAudioInputs();
      setAudioInputs(list);
      if (list.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(list[0].deviceId);
      }
    } catch {
      // permission may not be granted yet; we'll re-list after first start
    }
  }

  useEffect(() => {
    void ensureAudioInputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoRecordTriggered = useRef(false);
  useEffect(() => {
    if (autoRecord && !autoRecordTriggered.current) {
      autoRecordTriggered.current = true;
      const timer = setTimeout(() => { void startRecording(); }, 150);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRecord]);

  /**
   * The practice has no AI set up. `explain` opens the pop-up; background work
   * (suggestions after Stop or on leaving the editor) only records it, so the
   * pop-up appears when the user asks for AI, never on its own.
   */
  function markAiUnavailable(explain: boolean) {
    aiUnavailableRef.current = true;
    if (explain) setAiBlockedOpen(true);
  }

  async function startRecording() {
    if (sessionRef.current) return;
    try {
      const creds = await notesSvc.getTranscribeStreamCredentials();
      aiUnavailableRef.current = false;
      const session = new TranscribeSession({
        credentials: creds,
        deviceId: selectedDeviceId || undefined,
        onStatus: (s) => setRecStatus(s),
        onDeviceInUse: (id) => setSelectedDeviceId(id),
        onError: (err) => {
          console.error("Transcribe error", err);
          toast.error(describeDictationError(err).message);
          setRecStatus("idle");
        },
        onItem: (item) => {
          if (item.isPartial) {
            setPartialEntry(item);
          } else {
            setPartialEntry(null);
            setRecordedEntries((prev) => [
              ...prev,
              {
                id: item.id,
                speakerLabel: item.speakerLabel,
                text: item.text,
                startMs: item.startMs,
                endMs: item.endMs,
              },
            ]);
          }
        },
      });
      sessionRef.current = session;
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      // Don't await — `start()` runs the read loop until `stop()` is called.
      void session.start()
        .catch((err) => {
          console.error("Recording session failed to start", err);
          toast.error(describeDictationError(err).message);
          setRecStatus("idle");
        })
        .finally(() => {
          sessionRef.current = null;
        });
      // Refresh device labels once permission is granted.
      setTimeout(() => void ensureAudioInputs(), 500);
    } catch (err) {
      // Reached when the credentials call fails, before the microphone is ever
      // touched — so this must never blame microphone permissions.
      sessionRef.current = null;
      setRecStatus("idle");
      if (describeNoteAiError(err).kind === "not-configured") {
        markAiUnavailable(true);
        return;
      }
      console.error("Failed to start recording", err);
      toast.error(describeDictationError(err).message);
    }
  }

  async function stopRecording() {
    const session = sessionRef.current;
    if (!session) return;
    await session.stop();
    sessionRef.current = null;
    lastSourceRef.current = "transcript";

    // After stop: kick off Bedrock-driven suggestions in the sidebar.
    setTimeout(() => void runPostStopSuggestions(), 200);
  }

  // The minimized note widget calls these. They are registered once, so they
  // go through a ref to the latest render's functions, which read current
  // state (the transcript, the chosen microphone).
  const recordingFnsRef = useRef({ start: startRecording, stop: stopRecording });
  recordingFnsRef.current = { start: startRecording, stop: stopRecording };

  useEffect(() => {
    noteForm.registerRecordingControls({
      start: () => { void recordingFnsRef.current.start(); },
      stop: () => { void recordingFnsRef.current.stop(); },
    });
    return () => noteForm.registerRecordingControls(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fired from a timer after Stop: read the latest transcript and suggestions
  // runner through refs, so lines that land after Stop are included.
  function runPostStopSuggestions() {
    runSuggestionsRef.current(transcriptDraftRef.current, "transcript");
  }

  // Generate AI sidebar suggestions from an arbitrary source text — either the
  // dictation transcript (post-stop) or the typed note text (on editor blur).
  async function runSuggestions(sourceText: string, from: AiSource) {
    const source = sourceText.trim();
    if (!source) return;
    if (aiUnavailableRef.current) return;
    aiSourceRef.current = sourceText;
    setAiOptionsError("");

    // Auto-classify type if it's still unset.
    if (typeId === "0") {
      try {
        const result = await notesSvc.classifyNoteType(
          source,
          visibleCategories.map((c) => ({ noteTypeId: c.noteTypeID, name: c.name }))
        );
        if (result.noteTypeId && (result.confidence === "high" || result.confidence === "medium")) {
          setTypeId(String(result.noteTypeId));
          const matched = visibleCategories.find((c) => c.noteTypeID === result.noteTypeId);
          if (matched) toast.success(`Type set to "${matched.name}".`);
        }
      } catch (err) {
        // best-effort
        if (describeNoteAiError(err).kind === "not-configured") markAiUnavailable(false);
      }
    }
    if (aiUnavailableRef.current) return;

    // Suggest templates.
    if (templates.length > 0) {
      try {
        const result = await notesSvc.suggestTemplates(
          source,
          templates.slice(0, 60).map((t) => ({
            templateId: t.templateId,
            title: t.title,
            categoryName: t.categoryName,
          }))
        );
        setTplSuggestions(result.suggestions ?? []);
        setTplTab("suggested");
      } catch (err) {
        // best-effort
        if (describeNoteAiError(err).kind === "not-configured") markAiUnavailable(false);
      }
    }
    if (aiUnavailableRef.current) return;

    // Suggest format options.
    setAiOptionsLoading(true);
    try {
      const result = await notesSvc.suggestFormats(source);
      aiOptionsSourceRef.current = from;
      setAiOptions(result.options ?? []);
    } catch (err) {
      setAiOptions([]);
      const described = describeNoteAiError(err);
      if (described.kind === "not-configured") markAiUnavailable(false);
      else setAiOptionsError(described.message);
    } finally {
      setAiOptionsLoading(false);
    }
  }

  // Keep the ref pointed at the latest closure so the once-attached editor blur
  // handler always runs a version that reads current state (templates, typeId).
  runSuggestionsRef.current = (src: string, from: AiSource) => { void runSuggestions(src, from); };

  // ─── AI format application ───────────────────────────────────────────────
  // The text for a source as it reads now (transcript edits included),
  // falling back to the other one when it is empty.
  function aiSourceText(from: AiSource | null): string {
    const transcript = transcriptDraftRef.current.trim();
    const note = (editorRef.current?.getText() ?? "").trim();
    return from === "note" ? note || transcript : transcript || note;
  }

  async function runFormatPreview(prompt: string, label: string, from: AiSource | null) {
    const transcript = aiSourceText(from);
    if (!transcript) {
      toast.error("Record or type something first.");
      return;
    }
    setPreviewLabel(label);
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewHtml("");
    try {
      const result = await notesSvc.formatTranscript(transcript, prompt);
      aiUnavailableRef.current = false;
      setPreviewHtml(result.html || "");
    } catch (err) {
      setPreviewOpen(false);
      const described = describeNoteAiError(err);
      if (described.kind === "not-configured") markAiUnavailable(true);
      else toast.error(described.message);
    } finally {
      setPreviewLoading(false);
    }
  }

  function applyPreview(mode: "insert" | "replace") {
    const editor = editorRef.current;
    if (!editor || !previewHtml) return;
    if (mode === "insert") {
      editor.commands.insertContent(previewHtml);
    } else {
      editor.commands.setContent(previewHtml);
      setContent(previewHtml);
    }
    setPreviewOpen(false);
    toast.success(mode === "insert" ? "Inserted at cursor" : "Editor replaced");
  }

  // ─── E-Forms ─────────────────────────────────────────────────────────────
  async function openEForms() {
    setEformsOpen(true);
    if (eformPlans.length > 0 || eformSurveys.length > 0) return;
    setEformsLoading(true);
    try {
      const [plans, surveys] = await Promise.all([
        notesSvc.getEFormPlans(patientId),
        notesSvc.listSurveysForEForms(),
      ]);
      setEformPlans(plans);
      setEformSurveys(surveys.filter((s) => s.isActive !== false));
      if (plans.length > 0) setEformSelectedPlan(String(plans[0].planId));
    } catch {
      toast.error("Failed to load e-forms");
    } finally {
      setEformsLoading(false);
    }
  }

  async function openSurveyLink(surveyId: number, surveyTitle: string) {
    setEformLinkBusy(surveyId);
    try {
      // Capture the selected plan id at click time — the user might change
      // the dropdown while the form is open and the keyword replacement
      // step needs the value frozen here, exactly like legacy:
      //   onclick="notePlanID = $('#notePlanID2').val(); displaySurveyForNote(...)"
      const planIdRaw = eformSelectedPlan && eformSelectedPlan !== "0" ? parseInt(eformSelectedPlan, 10) : 0;
      notePlanIDRef.current = Number.isFinite(planIdRaw) ? planIdRaw : 0;

      const result = await notesSvc.createEFormLink(patientId, {
        surveyId,
        forNote: true,
      });
      setEformIframe({ url: result.fullUrl, title: surveyTitle });
    } catch {
      toast.error("Failed to open form");
    } finally {
      setEformLinkBusy(null);
    }
  }

  // Listen for the iframe's postMessage. Mirrors legacy sendKeywordsForEform
  // -> replaceKeywordsForEform -> processEformForNote: receive the rendered
  // form HTML, run V4's keyword substitution, insert at the cursor, close.
  useEffect(() => {
    if (!eformIframe) return;

    const handler = async (event: MessageEvent) => {
      // Only trust messages from the legacy host serving the iframe.
      try {
        const formOrigin = new URL(eformIframe.url).origin;
        if (event.origin !== formOrigin) return;
      } catch {
        return;
      }

      const raw = event.data;
      if (typeof raw !== "string" || raw === "") return;

      setEformInserting(true);
      try {
        const planId = notePlanIDRef.current > 0 ? notePlanIDRef.current : undefined;
        const { content: replaced } = await notesSvc.replaceEFormKeywords(patientId, {
          planId,
          content: raw,
        });

        const editor = editorRef.current;
        if (editor) {
          editor.commands.insertContent(replaced);
          setContent(editor.getHTML());
        } else {
          setContent((prev) => prev + replaced);
        }
        toast.success("Form inserted into note");
      } catch (e) {
        console.error("E-form keyword replacement failed", e);
        toast.error("Failed to insert form into note");
      } finally {
        setEformInserting(false);
        setEformIframe(null);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [eformIframe, patientId]);

  const recording = recStatus === "listening" || recStatus === "requesting-mic" || recStatus === "stopping";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="note-form-modal-title"
        className="relative bg-background border shadow-2xl rounded-lg max-w-6xl w-[92vw] sm:max-w-none max-h-[90vh] flex flex-col overflow-hidden p-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Attachment preview overlays the form rather than replacing it, so the
            form stays mounted and unsaved title/body/pending files survive —
            reading an attachment while transcribing from it is the whole point. */}
        {previewAttachment && (
          <div className="absolute inset-0 z-20 bg-background">
            <LabPdfPreviewPanel
              patientId={Number(patientId)}
              target={{
                kind: "attachment",
                patientId: Number(patientId),
                attachmentId: previewAttachment.attachmentId,
                title: previewAttachment.filename,
              }}
              onClose={() => setPreviewAttachment(null)}
              onNewTab={() => setPreviewAttachment(null)}
            />
          </div>
        )}

        <div className="flex items-center px-6 pt-6 pb-2">
          <h2
            id="note-form-modal-title"
            className="flex shrink-0 items-center gap-2 text-lg font-semibold leading-none tracking-tight"
          >
            <FileText className="h-5 w-5" />
            {isEdit ? "Edit Note" : "New Note"}
          </h2>
          {/* Which appointment this note is (or will be) linked to. The note form is
              a global singleton that survives route changes and can be re-expanded
              from the minimized card long after the appointment modal is gone, so
              without this there is no way to tell. Constrained because this row has
              no gap/flex-wrap and the panel is overflow-hidden — an unbounded label
              would clip the Minimize/Close buttons off the right edge. */}
          {apptChipLabel && (
            <Badge
              variant="secondary"
              className="ml-2 min-w-0 max-w-[280px] shrink font-normal"
              title={apptChipLabel}
            >
              <Calendar className="mr-1 h-3 w-3 shrink-0" />
              <span className="truncate">{apptChipLabel}</span>
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-1">
            {onMinimize && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onMinimize}
                className="h-8 w-8 rounded-full hover:bg-muted"
                title="Minimize"
                aria-label="Minimize"
              >
                <Minus className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 rounded-full hover:bg-muted"
              title="Close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_300px] gap-0 flex-1 min-h-0 overflow-hidden">
            {/* ─── Main column ─────────────────────────────────────────── */}
            <div ref={formScrollerRef} className="overflow-y-auto px-6 pb-6 space-y-4">
              {/* Toolbar */}
              <div className="flex items-center flex-wrap gap-2 pb-2 border-b sticky top-0 bg-background z-10">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleVisibilityToggle}
                  className="gap-1"
                >
                  {visibleToPatient ? "Visible to Patient" : "Hidden from Patient"}
                </Button>
                <Button variant="outline" size="sm" onClick={openEForms} className="gap-1">
                  <FileText className="h-3.5 w-3.5" /> E-Forms
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (typeId === "0") { toast.error("Pick a Note Type before saving as a template"); return; }
                    setSaveTemplateOpen(true);
                  }}
                  className="gap-1"
                >
                  Save as Template
                </Button>
                {/* SCRUM-1444: the action row above IS `flex-wrap`, and this spacer is what
                    made it read as three ragged rows — once wrapping is active the spacer
                    becomes a wrapped flex item and absorbs a whole line of free space, so the
                    buttons land in uneven groups instead of a compact block. Hidden below `md`;
                    above it, it is still what pushes Save & Print / Save to the right. */}
                <div className="flex-1 max-md:hidden" />
                <Button variant="outline" size="sm" onClick={() => handleSave(true)} disabled={saving} className="gap-1">
                  Save & Print
                </Button>
                <Button size="sm" onClick={() => handleSave(false)} disabled={saving} className="gap-1">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                </Button>
              </div>

              {/* Recording panel */}
              <RecordingPanel
                recording={recording}
                status={recStatus}
                elapsedMs={elapsedMs}
                audioInputs={audioInputs}
                selectedDeviceId={selectedDeviceId}
                onSelectDevice={setSelectedDeviceId}
                onStart={startRecording}
                onStop={stopRecording}
                onInsert={() => {
                  const text = transcriptDraft.trim();
                  if (!text) return;
                  const editor = editorRef.current;
                  if (editor) {
                    // The draft is typed text now, so it is escaped before the
                    // editor parses it as HTML.
                    editor.commands.insertContent(escapeHtml(text).replace(/\n/g, "<br>"));
                    setContent(editor.getHTML());
                  } else {
                    setContent((prev) => prev + escapeHtml(text));
                  }
                }}
                entries={recordedEntries}
                partialEntry={partialEntry}
                draft={transcriptDraft}
                onDraftChange={handleDraftChange}
              />

              {/* E-Forms panel */}
              {eformsOpen && (
                <EFormsPanel
                  loading={eformsLoading}
                  plans={eformPlans}
                  surveys={eformSurveys}
                  selectedPlan={eformSelectedPlan}
                  onSelectPlan={setEformSelectedPlan}
                  onClose={() => setEformsOpen(false)}
                  onPick={(id, title) => openSurveyLink(id, title)}
                  busyId={eformLinkBusy}
                />
              )}

              {/* Form */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div ref={typeRef} className="space-y-1.5 sm:col-span-1">
                  <Label>Type</Label>
                  <SearchableCombobox<string>
                    portal={false}
                    mode="single"
                    options={typeOptions}
                    value={typeId === "0" ? null : typeId}
                    onChange={(v) => { setTypeId(v ?? "0"); if (fieldErrors.typeId) setFieldErrors((p) => ({ ...p, typeId: undefined })); }}
                    placeholder="Select one..."
                    emptyMessage="No matching types"
                    searchByLabelOnly
                    className={fieldErrors.typeId ? "border-red-500" : ""}
                  />
                  {fieldErrors.typeId && <p className="text-sm font-medium text-red-600">{fieldErrors.typeId}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="note-form-date">Date</Label>
                  <DatePicker id="note-form-date" value={noteDate} onChange={setNoteDate} className="border-input bg-background" />
                </div>
                <div className="space-y-1.5">
                  <Label>Time</Label>
                  <Input type="time" value={noteTime} onChange={(e) => setNoteTime(e.target.value)} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Subject</Label>
                <Input
                  ref={subjectRef}
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); if (fieldErrors.title) setFieldErrors((p) => ({ ...p, title: undefined })); }}
                  placeholder="Enter subject..."
                  className={fieldErrors.title ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {fieldErrors.title && <p className="text-sm font-medium text-red-600">{fieldErrors.title}</p>}
              </div>

              {!isEdit && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={capturePhoto} onCheckedChange={(v) => setCapturePhoto(!!v)} />
                  Capture photo(s) from device after saving
                </label>
              )}

              <div className="space-y-1.5">
                <Label>Note</Label>
                <TiptapEditor
                  content={content}
                  onChange={(html) => setContent(html)}
                  editorRef={editorRef}
                  className="min-h-[260px]"
                />
              </div>

              {/* Existing Attachments (edit) */}
              {isEdit && note && note.attachments.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Existing Attachments</Label>
                  <div className="border rounded-md p-3 bg-muted/30 space-y-1">
                    {note.attachments
                      .filter((a) => !removeFileIds.includes(a.attachmentId))
                      .map((a) => (
                        <div key={a.attachmentId} className="flex items-center justify-between text-xs">
                          <button
                            type="button"
                            onClick={() => {
                              // PDFs/images preview in place; anything the browser
                              // can't render (.docx, .label) downloads instead.
                              if (isPreviewableFilename(a.filename)) {
                                setPreviewAttachment({ attachmentId: a.attachmentId, filename: a.filename });
                                return;
                              }
                              void notesSvc.downloadAttachment(patientId, a.attachmentId, a.filename)
                                .catch((err) => toast.error(err instanceof Error ? err.message : "Failed to download attachment"));
                            }}
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            <Paperclip className="h-3 w-3" />
                            {a.filename}
                          </button>
                          <button
                            type="button"
                            onClick={() => setRemoveFileIds((p) => [...p, a.attachmentId])}
                            className="text-destructive hover:text-destructive/80 p-1"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* File Upload */}
              <div className="space-y-1.5">
                <Label>{isEdit ? "Add Attachments" : "Attachments"}</Label>
                <FileUpload
                  multiple
                  maxSize={30 * 1024 * 1024}
                  onChange={(files) => setPendingFiles(files)}
                  disabled={saving}
                />
              </div>

              {/* Note History (edit) */}
              {isEdit && history.length > 0 && (
                <>
                  <Separator />
                  <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold">
                      <History className="h-4 w-4" /> Note History ({history.length})
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 space-y-2 pl-6">
                      {history.map((h) => (
                        <div key={h.historyId} className="border rounded-md p-2 text-xs">
                          <strong>{h.authorName || "System"}</strong> {"\u2014"}{" "}
                          {h.dateCreated ? new Date(h.dateCreated).toLocaleString() : "\u2014"}
                          <div className="mt-1 text-muted-foreground">{h.title}</div>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                </>
              )}
            </div>

            {/* ─── Right sidebar ───────────────────────────────────────── */}
            <aside className="hidden lg:flex flex-col border-l bg-muted/20 overflow-y-auto">
              <AiOptionsCard
                loading={aiOptionsLoading}
                options={aiOptions}
                error={aiOptionsError}
                customPrompt={customPrompt}
                onCustomPromptChange={setCustomPrompt}
                onPickStandard={(o) => runFormatPreview(o.prompt, o.label, lastSourceRef.current)}
                onPickSuggestion={(o) => runFormatPreview(o.prompt, o.label, aiOptionsSourceRef.current)}
                onCustomRun={() => {
                  if (customPrompt.trim()) runFormatPreview(customPrompt.trim(), "Custom prompt", lastSourceRef.current);
                }}
              />

              <Separator />

              <TemplatesCard
                tplTab={tplTab}
                onTabChange={(v) => setTplTab(v as typeof tplTab)}
                hasSuggestions={tplSuggestions.length > 0}
                suggested={suggestedTemplates}
                allFiltered={filteredTemplates}
                tplSearch={tplSearch}
                onSearchChange={setTplSearch}
                tplTypeFilter={tplTypeFilter}
                onTypeChange={setTplTypeFilter}
                categories={visibleCategories}
                onApply={handleApplyTemplate}
                currentUserId={currentUserId}
                onDelete={handleDeleteTemplate}
              />
            </aside>
          </div>
        )}

        {saveTemplateOpen && (
          <SaveTemplateDialog
            initialTitle={title}
            initialContent={editorRef.current?.getHTML() ?? content}
            noteTypeId={parseInt(typeId) || 0}
            onSaved={refetchTemplates}
            onClose={() => setSaveTemplateOpen(false)}
          />
        )}
      </div>

      {/* AI preview Sheet */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              {previewLabel || "AI Preview"}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4">
            {previewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner />
              </div>
            ) : (
              <div
                className="prose prose-sm max-w-none border rounded-md p-3 bg-background"
                /* AI output is sanitized server-side to a strict tag whitelist
                   (p, ul, ol, li, h2, h3, strong, em, br) before reaching us. */
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            )}
          </div>
          <div className="border-t p-4 flex flex-wrap gap-2 justify-end">
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => applyPreview("insert")} disabled={!previewHtml || previewLoading}>
              Apply (insert at cursor)
            </Button>
            <Button onClick={() => applyPreview("replace")} disabled={!previewHtml || previewLoading}>
              Replace editor
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={aiBlockedOpen} onOpenChange={setAiBlockedOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>AI isn&apos;t available</AlertDialogTitle>
            <AlertDialogDescription>{AI_NOT_CONFIGURED_MESSAGE}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* E-form iframe Sheet — mirrors legacy displaySurveyForNote() popup. */}
      <Sheet open={!!eformIframe} onOpenChange={(open) => { if (!open) setEformIframe(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
          <SheetHeader className="px-4 py-3 border-b">
            <SheetTitle className="text-sm">{eformIframe?.title ?? "E-Form"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0 relative">
            {eformInserting && (
              <div className="absolute inset-0 z-10 bg-background/70 flex items-center justify-center">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Inserting form into note…
                </div>
              </div>
            )}
            {eformIframe && (
              <iframe
                src={eformIframe.url}
                title={eformIframe.title}
                className="w-full h-full border-0"
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Hidden plain transcript for accessibility / screen readers */}
      <span className="sr-only">{transcriptDraft}</span>

      {/* Post-save photo capture — v5-native replacement for the legacy
          /patient_photo_capture.php popup. Each accepted shot is uploaded
          as a note attachment via the same endpoint used by the regular
          file picker, so it lands in notes_attachments just like the
          legacy patient_photo_save.php did. */}
      <CameraCaptureModal
        isOpen={cameraOpen}
        multi
        onCapture={(file) => {
          if (!cameraNoteId) return;
          void notesSvc
            .uploadAttachment(patientId, cameraNoteId, file, { mirrorToPhotos: true })
            .then(() => toast.success("Photo attached"))
            .catch(() => toast.error("Failed to attach photo"));
        }}
        onClose={() => {
          setCameraOpen(false);
          setCameraNoteId(null);
          noteForm.forceClose();
        }}
      />

      {printNoteId !== null && (
        <PrintOptionsDialog
          patientId={patientId}
          noteIds={String(printNoteId)}
          mode="pdf"
          onClose={() => {
            setPrintNoteId(null);
            noteForm.forceClose();
          }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Recording panel
// ────────────────────────────────────────────────────────────────────────────

function RecordingPanel({
  recording,
  status,
  elapsedMs,
  audioInputs,
  selectedDeviceId,
  onSelectDevice,
  onStart,
  onStop,
  onInsert,
  entries,
  partialEntry,
  draft,
  onDraftChange,
}: {
  recording: boolean;
  status: TranscribeSessionStatus | "idle";
  elapsedMs: number;
  audioInputs: { deviceId: string; label: string }[];
  selectedDeviceId: string;
  onSelectDevice: (id: string) => void;
  onStart: () => void;
  onStop: () => void;
  onInsert: () => void;
  entries: RecordedEntry[];
  partialEntry: LiveTranscriptItem | null;
  draft: string;
  onDraftChange: (v: string) => void;
}) {
  const labelled = hasSeveralSpeakers(partialEntry ? [...entries, partialEntry] : entries);
  const canInsert = !recording && draft.trim() !== "";
  return (
    <div className="border rounded-md bg-background">
      {/* SCRUM-1444: three items in one non-wrapping row — a nowrap Stop button, a free-flowing
          status span and a hard 200px mic selector as the last item. So the selector was the
          piece pushed past the modal's right edge, and the status span, being the only
          shrinkable thing in the row, was what wrapped to two lines. */}
      <div className="flex items-center gap-2 p-3 max-md:flex-wrap">
        {recording ? (
          <Button onClick={onStop} variant="destructive" size="sm" className="gap-1">
            <Square className="h-3.5 w-3.5 fill-current" /> Stop ({formatElapsed(elapsedMs)})
          </Button>
        ) : (
          <Button onClick={onStart} size="sm" className="gap-1">
            <Mic className="h-3.5 w-3.5" /> Record
          </Button>
        )}
        <span className="text-xs text-muted-foreground max-md:min-w-0">
          {status === "requesting-mic" && "Requesting microphone…"}
          {status === "listening" && "Live dictation active"}
          {status === "stopping" && "Stopping…"}
          {(status === "idle" || status === "stopped") && entries.length === 0 && "Press Record to dictate"}
          {status === "error" && "Error — please try again"}
        </span>
        <div className="flex-1" />
        {canInsert && (
          <Button variant="outline" size="sm" className="gap-1" onClick={onInsert}>
            <FileText className="h-3.5 w-3.5" /> Insert into note
          </Button>
        )}
        {audioInputs.length > 0 && (
          <Select value={selectedDeviceId} onValueChange={onSelectDevice} disabled={recording}>
            <SelectTrigger
              className="h-8 w-[200px] max-w-[200px] text-xs [&>span:first-child]:!block [&>span:first-child]:truncate [&>span:first-child]:min-w-0 max-md:w-full max-md:max-w-none"
              title={audioInputs.find((d) => d.deviceId === selectedDeviceId)?.label || "Microphone"}
            >
              <SelectValue placeholder="Microphone" />
            </SelectTrigger>
            <SelectContent>
              {audioInputs.map((d) => (
                <SelectItem key={d.deviceId} value={d.deviceId} className="text-xs">
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {recording && (
        <div className="border-t px-3 py-2 min-h-[15rem] max-h-[24rem] overflow-y-auto space-y-1">
          {draft && <div className="text-xs leading-relaxed whitespace-pre-wrap">{draft}</div>}
          {partialEntry && (
            <div className="text-xs leading-relaxed text-muted-foreground italic">
              {transcriptLine(partialEntry, labelled)}
            </div>
          )}
          {!partialEntry && (
            <div className="text-xs italic text-muted-foreground flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-destructive animate-pulse" />
              Listening for next sentence…
            </div>
          )}
        </div>
      )}
      {!recording && (entries.length > 0 || draft !== "") && (
        <Textarea
          aria-label="Transcript"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          className="min-h-[15rem] resize-y rounded-none rounded-b-md border-0 border-t text-xs leading-relaxed shadow-none focus-visible:ring-0"
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// E-Forms panel
// ────────────────────────────────────────────────────────────────────────────

/**
 * Builds the plan dropdown label exactly like legacy
 * note-load-eform-display.php (line 204):
 *   "Plan #{displayID} - {planTypeName} - {procName} (+N more)"
 * The "(+N more)" suffix is only added when procQuantity > 1.
 */
function formatLegacyPlanLabel(p: EFormPlan): string {
  const display = p.displayId || String(p.planId);
  const parts: string[] = [`Plan #${display}`];
  if (p.planTypeName) parts.push(p.planTypeName);
  let procPart = p.procName || "";
  if (procPart && p.procQuantity > 1) {
    procPart = `${procPart} (+${p.procQuantity - 1} more)`;
  }
  if (procPart) parts.push(procPart);
  return parts.join(" - ");
}

function EFormsPanel({
  loading,
  plans,
  surveys,
  selectedPlan,
  onSelectPlan,
  onClose,
  onPick,
  busyId,
}: {
  loading: boolean;
  plans: EFormPlan[];
  surveys: SurveyOption[];
  selectedPlan: string;
  onSelectPlan: (v: string) => void;
  onClose: () => void;
  onPick: (id: number, title: string) => void;
  busyId: number | null;
}) {
  return (
    <div className="border rounded-md p-3 bg-muted/30 space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold">E-Forms</p>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={onClose} className="h-7 w-7 p-0">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Plan</Label>
        <Select value={selectedPlan} onValueChange={onSelectPlan}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="No Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">No Plan</SelectItem>
            {plans.map((p) => (
              <SelectItem key={p.planId} value={String(p.planId)} className="text-xs">
                {formatLegacyPlanLabel(p)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="border rounded-md max-h-56 overflow-y-auto bg-background">
          {surveys.length === 0 && <p className="text-xs text-muted-foreground p-3">No e-forms available.</p>}
          {surveys.map((s) => (
            <button
              key={s.id}
              type="button"
              disabled={busyId !== null}
              onClick={() => onPick(s.id, s.title)}
              className="w-full flex items-center justify-between text-left text-xs px-3 py-2 border-b last:border-b-0 hover:bg-primary/5 transition-colors"
            >
              <span className="truncate">{s.title}</span>
              {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Templates sidebar card
// ────────────────────────────────────────────────────────────────────────────

function TemplatesCard({
  tplTab,
  onTabChange,
  hasSuggestions,
  suggested,
  allFiltered,
  tplSearch,
  onSearchChange,
  tplTypeFilter,
  onTypeChange,
  categories,
  onApply,
  currentUserId,
  onDelete,
}: {
  tplTab: "all" | "office" | "mine" | "suggested";
  onTabChange: (v: string) => void;
  hasSuggestions: boolean;
  suggested: { tpl: NoteTemplate; score: number; reason: string | null }[];
  allFiltered: NoteTemplate[];
  tplSearch: string;
  onSearchChange: (v: string) => void;
  tplTypeFilter: string;
  onTypeChange: (v: string) => void;
  categories: NoteCategory[];
  onApply: (id: number) => void;
  currentUserId: number;
  onDelete: (id: number) => void;
}) {
  const categoryOptions = useMemo<ComboboxOption<string>[]>(
    () => categories.map((c) => ({ value: String(c.noteTypeID), label: c.name })),
    [categories],
  );

  // Inline two-step delete confirm: first click arms the row, a second click
  // within a few seconds deletes; the armed state auto-resets on timeout.
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const confirmTimer = useRef<number | null>(null);
  const armDelete = (id: number) => {
    setConfirmingId(id);
    if (confirmTimer.current) window.clearTimeout(confirmTimer.current);
    confirmTimer.current = window.setTimeout(() => setConfirmingId(null), 3000);
  };
  useEffect(
    () => () => { if (confirmTimer.current) window.clearTimeout(confirmTimer.current); },
    [],
  );

  return (
    <div className="p-3 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Templates</p>
      <Tabs value={tplTab} onValueChange={onTabChange}>
        <TabsList className="grid grid-cols-4 h-8">
          <TabsTrigger value="all" className="text-[10px] h-7 px-1">All</TabsTrigger>
          <TabsTrigger value="office" className="text-[10px] h-7 px-1">Office</TabsTrigger>
          <TabsTrigger value="mine" className="text-[10px] h-7 px-1">Mine</TabsTrigger>
          <TabsTrigger value="suggested" disabled={!hasSuggestions} className="text-[10px] h-7 px-1">
            Suggested{hasSuggestions ? ` (${suggested.length})` : ""}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tplTab === "suggested" ? (
        <div className="mt-3 space-y-1.5">
          {suggested.length === 0 && (
            <p className="text-xs text-muted-foreground">Record or type a note to get suggestions.</p>
          )}
          {suggested.map(({ tpl, score, reason }) => (
            <button
              key={tpl.templateId}
              type="button"
              onClick={() => onApply(tpl.templateId)}
              className="w-full text-left text-xs border rounded-md p-2 bg-background hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{tpl.title}</span>
                <Badge variant="secondary" className="text-[10px]">{Math.round(score * 100)}%</Badge>
              </div>
              {reason && <p className="text-muted-foreground mt-1 line-clamp-2">{reason}</p>}
              {tpl.categoryName && <p className="text-muted-foreground mt-0.5 text-[10px]">{tpl.categoryName}</p>}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <SearchableCombobox<string>
            portal={false}
            mode="single"
            options={categoryOptions}
            value={tplTypeFilter === "all" ? null : tplTypeFilter}
            onChange={(v) => onTypeChange(v ?? "all")}
            placeholder="All categories"
            emptyMessage="No matching categories"
            searchByLabelOnly
            clearable
          />
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              value={tplSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search templates"
              className="h-8 pl-7 text-xs"
            />
          </div>

          <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
            {allFiltered.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {tplTab === "all" && tplTypeFilter === "all" && !tplSearch.trim()
                  ? "Pick a category to see its templates."
                  : "No templates match."}
              </p>
            )}
            {allFiltered.map((t) => {
              const isMine = !t.isShared && t.userId === currentUserId;
              return (
                <div key={t.templateId} className="relative group">
                  <button
                    type="button"
                    onClick={() => onApply(t.templateId)}
                    className="w-full text-left text-xs border rounded-md p-2 bg-background hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <div className="font-medium truncate pr-6">{t.title}</div>
                    <div className="text-muted-foreground text-[10px] mt-0.5 flex items-center gap-1">
                      {t.categoryName && <span className="truncate">{t.categoryName}</span>}
                      {isMine && (
                        <Badge variant="outline" className="ml-auto text-[10px]">Mine</Badge>
                      )}
                      {t.isShared && (
                        <Badge variant="outline" className="ml-auto text-[10px]">Office</Badge>
                      )}
                    </div>
                  </button>
                  {isMine && (
                    <div className="absolute top-1 right-1">
                      {confirmingId === t.templateId ? (
                        <button
                          type="button"
                          title="Confirm delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmingId(null);
                            onDelete(t.templateId);
                          }}
                          className="text-[10px] h-5 px-1.5 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                        >
                          Confirm?
                        </button>
                      ) : (
                        <button
                          type="button"
                          title="Delete template"
                          onClick={(e) => {
                            e.stopPropagation();
                            armDelete(t.templateId);
                          }}
                          className="opacity-0 group-hover:opacity-100 h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// AI options sidebar card
// ────────────────────────────────────────────────────────────────────────────

function AiOptionsCard({
  loading,
  options,
  error,
  customPrompt,
  onCustomPromptChange,
  onPickStandard,
  onPickSuggestion,
  onCustomRun,
}: {
  loading: boolean;
  options: FormatOption[];
  error: string;
  customPrompt: string;
  onCustomPromptChange: (v: string) => void;
  onPickStandard: (o: { prompt: string; label: string }) => void;
  onPickSuggestion: (o: { prompt: string; label: string }) => void;
  onCustomRun: () => void;
}) {
  return (
    <div className="p-3 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">AI Options</p>

      <div className="grid grid-cols-2 gap-1.5">
        {STANDARD_AI_PROMPTS.map((p) => (
          <Button
            key={p.id}
            type="button"
            variant="outline"
            size="sm"
            className="h-auto min-h-8 justify-start gap-1.5 whitespace-normal px-2 py-1.5 text-left text-xs"
            onClick={() => onPickStandard(p)}
          >
            <Wand2 className="h-3 w-3 text-primary shrink-0" />
            {p.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-2">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-1.5">
          {error && <p className="text-xs text-muted-foreground">{error}</p>}
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onPickSuggestion(o)}
              className="w-full text-left text-xs border rounded-md p-2 bg-background hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-center gap-1.5">
                <Wand2 className="h-3 w-3 text-primary shrink-0" />
                <span className="font-medium truncate">{o.label}</span>
              </div>
              {o.description && <p className="text-muted-foreground mt-1 line-clamp-2">{o.description}</p>}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs">Custom prompt</Label>
        <Textarea
          value={customPrompt}
          onChange={(e) => onCustomPromptChange(e.target.value)}
          placeholder="e.g. Write a phone call note"
          className="text-xs min-h-[100px]"
        />
        <Button size="sm" className="w-full gap-1" disabled={!customPrompt.trim()} onClick={onCustomRun}>
          <Wand2 className="h-3 w-3" /> Run custom prompt
        </Button>
      </div>
    </div>
  );
}
